import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { Log } from "../../../utils";
import { ERR_INVALID_ARGUMENT } from "../../../utils/error";
import { serializeVector3 } from "../../io";
import { FileNotFoundError } from "../../io/Types";
import { GltfMesh } from "../io";
import { Matrix } from "@babylonjs/core/Maths/math.vector";
/** @internal */
export const KEY_IS_FALLBACK = "egr_wcf_isFallback";
/** @internal */
export function serializeGeometries(pManager, pContext) {
    if (pContext == null || pContext.geometryIds == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tIOGeoms = new Array();
    for (const tGeomId of pContext.geometryIds) {
        const tGeom = pManager.getGeometry(tGeomId);
        if (tGeom != null) {
            const tIOGeom = {};
            tIOGeom.id = tGeomId;
            tIOGeom.boundMin = serializeVector3(tGeom.extend.minimum);
            tIOGeom.boundMax = serializeVector3(tGeom.extend.maximum);
            tIOGeoms.push(tIOGeom);
        }
        else {
            Log.warn("Unknown geometry id: " + tGeomId);
            tIOGeoms.push({ id: tGeomId, boundMin: [0, 0, 0], boundMax: [0, 0, 0] });
        }
    }
    return (tIOGeoms);
}
/** @internal */
export async function addGeometriesToContainer(pManager, pGeometryIds, pContainer, pProgressCallback) {
    if (pGeometryIds == null || pContainer == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tCount = pGeometryIds.length;
    for (let i = 0; i < tCount; ++i) {
        await addGeometryToContainer(pManager, pGeometryIds[i], pContainer);
        if (pProgressCallback != null)
            pProgressCallback((i / tCount) * 100.0);
    }
}
/** @internal */
export async function readGeometriesFromContainer(pManager, pGeometries, pContainer, pProgressCallback) {
    if (pGeometries == null || pGeometries == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tCount = pGeometries.length;
    for (let i = 0; i < tCount; ++i) {
        await readGeometryFromContainer(pManager, pGeometries[i], pContainer);
        if (pProgressCallback != null)
            pProgressCallback((i / tCount) * 100.0);
    }
}
//// internals /////////////////////////////////////////////////////////////////////////////////////
function addGeometryToContainer(pManager, pGeomId, pContainer) {
    const tGeom = pManager.getGeometryMesh(pGeomId);
    if (tGeom == null) {
        Log.warn("Unknown geometry id: " + pGeomId);
        return (Promise.resolve());
    }
    if (tGeom.geometry == null) {
        Log.warn("Geometry mesh has no geometry: " + pGeomId);
        return (Promise.resolve());
    }
    //do not embed fallback geometries
    if (pManager.isFallbackGeometry(tGeom.geometry))
        return (Promise.resolve());
    const tFilename = "geometries/" + pGeomId + GltfMesh.BINARY_GLTF_EXTENSION;
    //try use geometry from cache first
    const tPromise = pContainer.addFileFromCache(tFilename, true).then((pAdded) => {
        if (pAdded) {
            return (Promise.resolve());
        }
        else {
            const tBuffer = GltfMesh.Export(tGeom);
            if (tBuffer == null) {
                Log.error("Failed to export geometry " + pGeomId);
                return (Promise.resolve());
            }
            return (pContainer.addFile(tBuffer, tFilename, true, true));
        }
    }).catch((pError) => {
        Log.error("Failed to embed geometry " + pGeomId, pError);
    });
    return (tPromise);
}
function readGeometryFromContainer(pManager, pGeom, pContainer) {
    if (pGeom == null || pManager.hasGeometry(pGeom.id))
        return (Promise.resolve());
    const tPromise = pContainer.getFile("geometries/" + pGeom.id + GltfMesh.BINARY_GLTF_EXTENSION, true).then((pBuffer) => {
        const tMeshes = GltfMesh.Import(pBuffer, pManager.scene);
        if (tMeshes != null && tMeshes.length > 0) {
            //exported gltf files should contain only one mesh
            pManager.addGeometryMesh(pGeom.id, tMeshes[0]);
        }
        else {
            Log.error("Failed to import geometry " + pGeom.id);
        }
    }).catch((pError) => {
        //create fallback geometry
        createFallbackGeometry(pManager, pGeom.id, pGeom);
        pManager.missingGeometries.add(pGeom.id);
        if (!(pError instanceof FileNotFoundError))
            Log.error("Failed to get embedded geometry " + pGeom.id, pError);
    });
    return (tPromise);
}
function createFallbackGeometry(pManager, pGeomId, pGeom) {
    if (pGeom == null || pManager.hasGeometry(pGeomId))
        return;
    const tMesh = CreateBox(pGeomId, {
        width: pGeom.boundMax[0] - pGeom.boundMin[0],
        height: pGeom.boundMax[1] - pGeom.boundMin[1],
        depth: pGeom.boundMax[2] - pGeom.boundMin[2],
        updatable: false
    }, pManager.scene);
    const tMTrans = Matrix.Translation((pGeom.boundMin[0] + pGeom.boundMax[0]) * 0.5, (pGeom.boundMin[1] + pGeom.boundMax[1]) * 0.5, (pGeom.boundMin[2] + pGeom.boundMax[2]) * 0.5);
    tMesh.bakeTransformIntoVertices(tMTrans);
    tMesh.geometry[KEY_IS_FALLBACK] = true;
    pManager.addGeometryMesh(pGeomId, tMesh);
}
